import { useState } from "react";
import { WretchError } from "wretch/resolver";
import Button from "../../components/Button";
import DeleteAccountDialog from "../../components/DeleteAccountDialog";
import Header from "../../components/Header";
import Listbox from "../../components/Listbox";
import Panel from "../../components/Panel";
import { useAuth } from "../../context/authContext";
import {
  usePreferences,
  usePreferencesMutation,
  useSubscription,
} from "../../hooks/useAccount";
import useToggle from "../../hooks/useToggle";
import {
  CountingFrom,
  CountingFromValues,
  TirePressureUnit,
  TirePressureUnits,
} from "../../http/onlyclicksApi";
import { translate } from "../../i18n";
import Page from "../../layouts/Page";
import { Language } from "../../types";
import Description, { DescriptionList } from "./Description";

const ProfilePage = () => {
  const { session, signOut } = useAuth();
  const [isSigningOut, setSigningOut] = useState(false);
  const i18n = translate(Language.English);
  const deleteDialog = useToggle();

  const { data: preferences, isSuccess: isPreferencesSuccess } =
    usePreferences();
  const mutation = usePreferencesMutation();
  const {
    data: subscription,
    isSuccess: isSubscriptionSuccess,
    isError: isSubscriptionError,
    error: subscriptionError,
  } = useSubscription();

  return (
    <>
      <Header heading="Profile" />
      <Page>
        <Panel title="">
          <p>{session?.email}</p>
        </Panel>
        <Panel title="Preferences">
          {isPreferencesSuccess && (
            <div>
              <DescriptionList>
                <Description
                  label="Counting from"
                  value={
                    <Listbox<CountingFrom>
                      values={CountingFromValues}
                      value={preferences.counting_from}
                      formatValue={i18n.countingFrom}
                      onChange={(countingFrom) => {
                        if (preferences.counting_from !== countingFrom) {
                          mutation.mutate({
                            ...preferences,
                            counting_from: countingFrom,
                          });
                        }
                      }}
                    />
                  }
                />
                <Description
                  label="Tire Pressure Unit"
                  value={
                    <Listbox<TirePressureUnit>
                      values={TirePressureUnits}
                      value={preferences.tire_pressure_unit}
                      formatValue={i18n.tirePressureUnit}
                      onChange={(tirePressureUnit) => {
                        if (
                          preferences.tire_pressure_unit !== tirePressureUnit
                        ) {
                          mutation.mutate({
                            ...preferences,
                            tire_pressure_unit: tirePressureUnit,
                          });
                        }
                      }}
                    />
                  }
                />
              </DescriptionList>
            </div>
          )}
        </Panel>
        <Panel title="Billing">
          {isSubscriptionSuccess && (
            <DescriptionList>
              <Description
                label="Plan"
                value={i18n.plan(subscription.plan_name)}
              />
            </DescriptionList>
          )}
          {isSubscriptionError && subscriptionError instanceof WretchError && (
            <>
              {subscriptionError.status === 404 &&
                "We could not find any invoices for your subscription"}
            </>
          )}
        </Panel>
        <Panel title="Delete account">
          <p>
            No longer want to use our service? You can delete your account here.
            This action is not reversible. All information related to this
            account will be deleted permanently.
          </p>
          <Button
            type="accent_secondary"
            fullWidth={true}
            buttonProps={{ onClick: deleteDialog.open }}
          >
            Yes, delete my account
          </Button>
        </Panel>
        <Panel>
          <Button
            type="accent"
            isLoading={isSigningOut}
            fullWidth={true}
            buttonProps={{
              disabled: isSigningOut,
              onClick: () => {
                setSigningOut(true);
                signOut().catch(() => setSigningOut(false));
              },
            }}
          >
            Logout
          </Button>
        </Panel>
      </Page>
      <DeleteAccountDialog
        accountId={session!.id}
        open={deleteDialog.isOpen}
        onClose={deleteDialog.close}
      />
    </>
  );
};

export default ProfilePage;
